import { toast } from "react-toastify";

import { CrmPetModel } from "../../models/crmPet";
import { CRM_PET_DEFAULT_FILTER, useCrmPet } from "../../../store/crmPet/useCrmPet";

export default class CrmPetControllerClass {
  constructor() {
    this.name = "crm-pet";
    this.tableName = `${this.name}-table`;
    this.paginationName = `${this.name}-pagination`;
    this.opportunitiesTableName = `${this.name}-opportunities-table`;
    this.opportunitiesPaginationName = `${this.name}-opportunities-pagination`;
    this.pendingTableName = `${this.name}-pending-table`;
    this.pendingPaginationName = `${this.name}-pending-pagination`;
    this.contactedTableName = `${this.name}-contacted-table`;
    this.contactedPaginationName = `${this.name}-contacted-pagination`;
    this.MCrmPet = CrmPetModel();
    this.init();
  }

  init = () => {
    if (typeof window !== "undefined") {
      const params = new URLSearchParams(window.location.search);
      useCrmPet.getState().actions.setFilter({
        rule: params.get("rule") || "ALL",
        analysisDays: params.get("analysisDays") || "30",
        contact: params.get("contact") || "ALL",
        subscription: params.get("subscription") || "ALL",
        categoryCode: params.get("categoryCode") || "ALL",
        search: params.get("search") || "",
      });
      this.syncFilterUrl();
    }
    this.findPets();
    this.findConfig();
  };

  findPets = async () => {
    return this.MCrmPet.findPets();
  };

  findEligibleOpportunities = async () => {
    return this.MCrmPet.findEligibleOpportunities();
  };

  loadEligibleOpportunities = async () => {
    const { pets, pagination, isWaiting } =
      useCrmPet.getState().data.opportunities;

    if (!isWaiting && !pets[pagination.page]) {
      await this.findEligibleOpportunities();
    }
  };

  findPendingContacts = async () => {
    return this.MCrmPet.findPendingContacts();
  };

  loadPendingContacts = async () => {
    const { pets, pagination, isWaiting } =
      useCrmPet.getState().data.pending;

    if (!isWaiting && !pets[pagination.page]) {
      await this.findPendingContacts();
    }
  };

  findContactedCustomers = async () => {
    return this.MCrmPet.findContactedCustomers();
  };

  loadContactedCustomers = async () => {
    const { pets, pagination, isWaiting } =
      useCrmPet.getState().data.contacted;

    if (!isWaiting && !pets[pagination.page]) {
      await this.findContactedCustomers();
    }
  };

  findConfig = async () => {
    return this.MCrmPet.findConfig();
  };

  saveConfig = async (config) => {
    const result = await this.MCrmPet.saveConfig(config);
    if (result) {
      const actions = useCrmPet.getState().actions;
      actions.setPaginationPage(0);
      actions.clearCustomers();
      await this.findPets();
    }
    return result;
  };

  findCustomerActivePets = async (customerId) => {
    return this.MCrmPet.findCustomerActivePets(customerId);
  };

  registerContact = async (payload) => {
    return this.MCrmPet.registerContact(payload);
  };

  registerUnifiedContact = async (customer, petIds, membershipIds, message) => {
    const actions = useCrmPet.getState().actions;
    actions.setContactSubmitting(true);
    try {
      const result = await this.registerContact({
        customerId: customer.customerId,
        petIds,
        membershipIds,
        message,
      });
      if (result) {
        actions.setPaginationPage(0);
        actions.clearCustomers();
        await this.findPets();
      }
      return result;
    } finally {
      actions.setContactSubmitting(false);
    }
  };

  registerPetContact = async (pet, contactDone = null) => {
    if (!pet?.petId) return null;

    const actions = useCrmPet.getState().actions;
    const currentDone = useCrmPet.getState().data.contactDone[pet.petId] ?? Boolean(pet.contatoRealizado);
    const done = contactDone ?? !currentDone;
    actions.setContactMarking(pet.petId, true);

    try {
      if (!done) {
        actions.setPetContactDone(pet.petId, false, null);
        return { success: true };
      }

      const result = await this.registerContact({
        petId: pet.petId,
        petName: pet.petName,
        customerId: pet.customerId,
        customerCpf: pet.customerCpf,
        customerName: pet.customerName,
        customerPhone: pet.customerPhone,
        daysSinceLastAppointment: pet.daysSinceLastAppointment,
        lastAppointmentDate: pet.lastAppointmentDate,
        responsavelId: null,
        responsavelNome: null,
      });

      if (!result) return null;

      actions.setPetContactDone(pet.petId, true, result.dataContato || new Date());
      return result;
    } finally {
      actions.setContactMarking(pet.petId, false);
    }
  };

  registerSelectedPetsContact = async (pet, selectedPets = []) => {
    return Promise.all(selectedPets.map((selectedPet) => (
      this.registerPetContact({ ...pet, ...selectedPet }, true)
    )));
  };

  openContactHistory = async (customer) => {
    useCrmPet.getState().actions.openContactHistory(customer);
    return this.MCrmPet.findContactHistory(customer);
  };

  closeContactHistory = () => {
    useCrmPet.getState().actions.closeContactHistory();
  };

  openWhatsApp = (customerKey) => {
    useCrmPet.getState().actions.setSelectedCustomerKey(customerKey);
  };

  closeWhatsApp = () => {
    useCrmPet.getState().actions.setSelectedCustomerKey(null);
  };

  setBlocked = async (petId, bloqueado = true) => {
    return this.MCrmPet.setBlocked(petId, bloqueado);
  };

  toggleBlocked = async (pet) => {
    if (!pet?.petId) return null;

    const actions = useCrmPet.getState().actions;
    const bloqueado = !(pet.blocked ?? pet.bloqueado);
    actions.setPetBlocking(pet.petId, true);

    try {
      const result = await this.setBlocked(pet.petId, bloqueado);
      if (!result) return null;

      actions.setPetBlocked(
        pet.petId,
        bloqueado,
        bloqueado ? result.dataBloqueio || new Date() : null,
      );
      actions.clearCustomers();
      await this.findPets();
      return result;
    } finally {
      actions.setPetBlocking(pet.petId, false);
    }
  };

  setFilter = (filterData) => {
    useCrmPet.getState().actions.setFilter(filterData);
  };

  setRuleTab = async (rule) => {
    const actions = useCrmPet.getState().actions;
    actions.setFilter({ rule, categoryCode: "ALL" });
    actions.setSort("customerName", "asc");
    actions.setPaginationPage(0);
    actions.clearCustomers();
    this.syncFilterUrl();
    return this.findPets();
  };

  setOpportunitiesFilter = (filterData) => {
    useCrmPet.getState().actions.setOpportunitiesFilter(filterData);
  };

  setPendingFilter = (filterData) => {
    useCrmPet.getState().actions.setPendingFilter(filterData);
  };

  setContactedFilter = (filterData) => {
    const nextFilter = { ...filterData };

    if (
      nextFilter.diasFiltro &&
      nextFilter.diasFiltro !== "custom"
    ) {
      nextFilter.customDays = nextFilter.diasFiltro;
    }

    useCrmPet.getState().actions.setContactedFilter(nextFilter);
  };

  filterPets = async (event) => {
    if (event) event.preventDefault();

    const actions = useCrmPet.getState().actions;
    actions.setLoading(true);
    actions.setPaginationPage(0);
    actions.clearCustomers();

    this.syncFilterUrl();

    await this.findPets();
  };

  clearFilter = async () => {
    const actions = useCrmPet.getState().actions;
    actions.clearFilter();
    actions.setPaginationPage(0);
    actions.clearCustomers();

    this.syncFilterUrl();

    await this.findPets();
  };

  filterEligibleOpportunities = async (event) => {
    if (event) event.preventDefault();

    const { from, to } =
      useCrmPet.getState().data.opportunities.filter;

    if (from && to && from > to) {
      toast.error("A data de início não pode ser posterior à data de fim.", {
        theme: "colored",
      });
      return null;
    }

    const actions = useCrmPet.getState().actions;
    actions.setOpportunitiesLoading(true);
    actions.setOpportunitiesPaginationPage(0);
    actions.clearOpportunitiesPets();

    return this.findEligibleOpportunities();
  };

  clearOpportunitiesFilter = async () => {
    const actions = useCrmPet.getState().actions;
    actions.clearOpportunitiesFilter();
    actions.setOpportunitiesPaginationPage(0);
    actions.clearOpportunitiesPets();

    return this.findEligibleOpportunities();
  };

  filterPendingContacts = async (event) => {
    if (event) event.preventDefault();

    const { from, to } = useCrmPet.getState().data.pending.filter;

    if (from && to && from > to) {
      toast.error("A data de início não pode ser posterior à data de fim.", {
        theme: "colored",
      });
      return null;
    }

    const actions = useCrmPet.getState().actions;
    actions.setPendingLoading(true);
    actions.setPendingPaginationPage(0);
    actions.clearPendingPets();

    return this.findPendingContacts();
  };

  clearPendingFilter = async () => {
    const actions = useCrmPet.getState().actions;
    actions.clearPendingFilter();
    actions.setPendingPaginationPage(0);
    actions.clearPendingPets();

    return this.findPendingContacts();
  };

  filterContactedCustomers = async (event) => {
    if (event) event.preventDefault();

    const { from, to } = useCrmPet.getState().data.contacted.filter;

    if (from && to && from > to) {
      toast.error("A data de início não pode ser posterior à data de fim.", {
        theme: "colored",
      });
      return null;
    }

    const actions = useCrmPet.getState().actions;
    actions.setContactedLoading(true);
    actions.setContactedPaginationPage(0);
    actions.clearContactedPets();

    return this.findContactedCustomers();
  };

  clearContactedFilter = async () => {
    const actions = useCrmPet.getState().actions;
    actions.clearContactedFilter();
    actions.setContactedPaginationPage(0);
    actions.clearContactedPets();

    return this.findContactedCustomers();
  };

  setPage = async (page) => {
    const state = useCrmPet.getState().data;
    const pageExists = state.customers[page];

    useCrmPet.getState().actions.setPaginationPage(page);

    if (!pageExists) await this.findPets();
  };

  setEligibleOpportunitiesPage = async (page) => {
    const { pets } = useCrmPet.getState().data.opportunities;
    const pageExists = pets[page];

    useCrmPet.getState().actions.setOpportunitiesPaginationPage(page);

    if (!pageExists) await this.findEligibleOpportunities();
  };

  setPendingPage = async (page) => {
    const { pets } = useCrmPet.getState().data.pending;
    const pageExists = pets[page];

    useCrmPet.getState().actions.setPendingPaginationPage(page);

    if (!pageExists) await this.findPendingContacts();
  };

  setContactedPage = async (page) => {
    const { pets } = useCrmPet.getState().data.contacted;
    const pageExists = pets[page];

    useCrmPet.getState().actions.setContactedPaginationPage(page);

    if (!pageExists) await this.findContactedCustomers();
  };

  handleSortColumn = async (sortBy) => {
    const { sort } = useCrmPet.getState().data;
    const sortOrder = sort.sortBy === sortBy && sort.sortOrder === "asc" ? "desc" : "asc";
    const actions = useCrmPet.getState().actions;

    actions.setSort(sortBy, sortOrder);
    actions.setPaginationPage(0);
    actions.clearCustomers();

    await this.findPets();
  };

  openConfig = () => {
    useCrmPet.getState().actions.setShowConfig(true);
  };

  closeConfig = () => {
    useCrmPet.getState().actions.setShowConfig(false);
  };

  reset = () => {
    useCrmPet.getState().actions.setFilter(CRM_PET_DEFAULT_FILTER);
  };

  syncFilterUrl = () => {
    if (typeof window === "undefined") return;
    const filter = useCrmPet.getState().data.filter;
    const params = new URLSearchParams(window.location.search);
    if (filter.rule && filter.rule !== "ALL") params.set("rule", filter.rule);
    else params.delete("rule");
    if (filter.analysisDays && filter.analysisDays !== "30") params.set("analysisDays", filter.analysisDays);
    else params.delete("analysisDays");
    if (filter.contact && filter.contact !== "ALL") params.set("contact", filter.contact);
    else params.delete("contact");
    if (filter.subscription && filter.subscription !== "ALL") params.set("subscription", filter.subscription);
    else params.delete("subscription");
    if (filter.categoryCode && filter.categoryCode !== "ALL") params.set("categoryCode", filter.categoryCode);
    else params.delete("categoryCode");
    if (filter.search) params.set("search", filter.search);
    else params.delete("search");
    params.delete("subtype");
    const query = params.toString();
    window.history.replaceState(null, "", `${window.location.pathname}${query ? `?${query}` : ""}`);
  };
}
